import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { getFriendlyFinanceErrorMessage } from '@/lib/financeErrors';

export interface Payment {
  id: string;
  deal_id: string;
  payment_number: string;
  installment_number: number;
  payment_type: string;
  description?: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'partially_paid' | 'paid' | 'overdue' | 'cancelled';
  stripe_payment_intent_id?: string;
  stripe_payment_link?: string;
  stripe_invoice_id?: string;
  sent_at?: string;
  paid_at?: string;
  paid_amount: number;
  balance: number;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Auto-charge fields
  auto_charge_enabled: boolean;
  auto_charge_status: string | null;
  auto_charge_attempts: number;
  last_charge_attempt_at?: string;
  last_charge_error?: string;
}

export function usePayments(dealId?: string) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  // NOTE: We no longer sync deal total_amount from payments sum.
  // The deal's total_amount is calculated from subtotal_amount + discount/markup,
  // and payments represent the collection plan (which may differ from contract value).
  // This function is kept as a no-op for backward compatibility with existing calls.
  const updateDealTotal = async () => {
    // Intentionally empty - total_amount is managed by item additions and discount changes
    // Payments are the COLLECTION PLAN, not the definition of deal value
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('payments')
        .select('*')
        .order('due_date', { ascending: true })
        .order('installment_number', { ascending: true });

      if (dealId) {
        query = query.eq('deal_id', dealId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setPayments(data as Payment[] || []);
    } catch (err) {
      setError(err as Error);
      toast({
        variant: 'destructive',
        title: 'Error loading payments',
        description: getFriendlyFinanceErrorMessage(err, 'Failed to load payments'),
      });
    } finally {
      setLoading(false);
    }
  };

  const createPayment = async (paymentData: any) => {
    try {
      const { data, error: insertError } = await supabase
        .from('payments')
        .insert([paymentData])
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: 'Payment created',
        description: 'The payment has been created successfully.',
      });

      await updateDealTotal();
      await loadPayments();
      return data;
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error creating payment',
        description: getFriendlyFinanceErrorMessage(err, 'Failed to create payment'),
      });
      throw err;
    }
  };

  const updatePayment = async (id: string, updates: any) => {
    try {
      const { error: updateError } = await supabase
        .from('payments')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Payment updated',
        description: 'The payment has been updated successfully.',
      });

      await updateDealTotal();
      await loadPayments();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error updating payment',
        description: getFriendlyFinanceErrorMessage(err, 'Failed to update payment'),
      });
      throw err;
    }
  };

  const deletePayment = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Payment deleted',
        description: 'The payment has been deleted successfully.',
      });

      await updateDealTotal();
      await loadPayments();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error deleting payment',
        description: getFriendlyFinanceErrorMessage(err, 'Failed to delete payment'),
      });
      throw err;
    }
  };

  const deleteAllPayments = async () => {
    if (!dealId) return;
    
    try {
      // 1. Get all payment IDs for this deal
      const { data: dealPayments, error: fetchError } = await supabase
        .from('payments')
        .select('id')
        .eq('deal_id', dealId);

      if (fetchError) throw fetchError;
      const paymentIds = (dealPayments || []).map(p => p.id);

      if (paymentIds.length > 0) {
        // 2. Unlink payment_transactions (keeps them visible for re-linking)
        const { error: unlinkTxError } = await supabase
          .from('payment_transactions')
          .update({ payment_id: null })
          .eq('deal_id', dealId);
        if (unlinkTxError) throw unlinkTxError;

        // 3. Unlink financial_entries
        const { error: unlinkFeError } = await supabase
          .from('financial_entries')
          .update({ payment_id: null })
          .in('payment_id', paymentIds);
        if (unlinkFeError) throw unlinkFeError;

        // 4. Unlink renegotiations
        const { error: unlinkRenego } = await supabase
          .from('renegotiations')
          .update({ original_payment_id: null })
          .in('original_payment_id', paymentIds);
        if (unlinkRenego) throw unlinkRenego;

        // 5. Unlink finance_recon_matches
        const { error: unlinkRecon } = await supabase
          .from('finance_recon_matches')
          .update({ payment_id: null })
          .in('payment_id', paymentIds);
        if (unlinkRecon) throw unlinkRecon;

        // 6. Unlink finance_recon_issues
        const { error: unlinkIssues } = await supabase
          .from('finance_recon_issues')
          .update({ payment_id: null })
          .in('payment_id', paymentIds);
        if (unlinkIssues) throw unlinkIssues;
      }

      // 7. Now safe to delete all payments
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('deal_id', dealId);

      if (deleteError) throw deleteError;

      toast({
        title: 'All payments deleted',
        description: 'All payments have been deleted successfully.',
      });

      await updateDealTotal();
      await loadPayments();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error deleting payments',
        description: getFriendlyFinanceErrorMessage(err, 'Failed to delete payments'),
      });
      throw err;
    }
  };

  useEffect(() => {
    loadPayments();
  }, [dealId]);

  return {
    payments,
    loading,
    error,
    loadPayments,
    createPayment,
    updatePayment,
    deletePayment,
    deleteAllPayments,
  };
}
